/**
 * Inline line icons for the sidebar navigation (16 px grid, 1.6 px stroke,
 * `currentColor`). No icon library: the set is tiny and must look the same in
 * both portals. Keys match `NavItem.icon` in nav.ts.
 */
const PATHS: Readonly<Record<string, string>> = {
  // Home / dashboard: four tiles.
  dashboard: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
  home: 'M3 11l9-8 9 8M5 10v10h5v-6h4v6h5V10',
  plus: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 8v8M8 12h8',
  // Orders: a cart.
  orders: 'M3 3h2l2.4 12.4a2 2 0 0 0 2 1.6h8.7a2 2 0 0 0 2-1.5L22 7H6M9 21h.01M18 21h.01',
  // Clients: an address book.
  clients: 'M4 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4zM4 9h-2M4 15h-2M11 13a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M7 18a4 4 0 0 1 8 0',
  // Agents: an id badge.
  agents: 'M3 5h18v14H3zM7 15a3 3 0 0 1 6 0M10 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4M15 9h3M15 13h3',
  // Products: a package.
  products: 'M16.5 9.4L7.5 4.2M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4a2 2 0 0 0 1-1.7M3.3 7l8.7 5 8.7-5M12 22V12',
  payments: 'M2 7h20v12H2zM2 11h20M6 15h4',
  reports: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  users: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 8.92a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8.92 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1',
  profile: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8M5.5 19.5a7 7 0 0 1 13 0',
};

export function NavIcon({ name }: { name?: string }) {
  const d = name ? PATHS[name] : undefined;
  if (!d) return <span aria-hidden="true">·</span>;
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

/** Small magnifier for search fields (sits inside `.input`). */
export function SearchIcon() {
  return (
    <svg className="search-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3" />
    </svg>
  );
}
